/**
 * Чистая логика раздела «Сцепки» (AK-747, форма 2 макета `machinery-forms`).
 *
 * Сцепка — ТИП агрегата из марок каталога (решение founder 26.08, развилка Р1),
 * а не набор конкретных машин: нормативы книги ЕТС ключуются марками.
 */

export type UnitMember = {
  model_id: string;
  name: string | null;
  type_name: string | null;
  count: number;
  working_width_m: number | null;
};

export type UnitNorm = {
  company_op_type_id: string | null;
  op_name: string | null;
  work_name_raw: string | null;
  params_raw: string | null;
  norm_per_shift: number | null;
  fuel_rate: number | null;
  source_workbook: string | null;
  effective_from: string | null;
};

export type MachineryUnit = {
  id: string;
  name: string;
  name_is_custom: boolean;
  prime_mover: UnitMember | null;
  members: UnitMember[];
  working_width_m: number | null;
  working_speed_kmh: number | null;
  operation_ids: string[];
  operation_names: string[];
  norms: UnitNorm[];
  has_norm: boolean;
  missing_norm_reasons: string[];
  archived_at: string | null;
};

export type UnitsPage = {
  total: number;
  with_norm: number;
  without_norm: number;
  items: MachineryUnit[];
};

/**
 * Состав словами: «1 трактор + 2 бороны».
 *
 * Не идентификаторы и не «prime_mover ×1»: колонка читается взглядом, и она
 * про то, из чего собран агрегат, а не про то, как он лежит в базе.
 */
export function compositionLabel(unit: MachineryUnit): string {
  const parts: string[] = [];
  if (unit.prime_mover) {
    parts.push(unit.prime_mover.type_name ? `1 ${unit.prime_mover.type_name.toLowerCase()}` : '1 машина');
  }
  for (const member of unit.members) {
    const kind = member.type_name ? member.type_name.toLowerCase() : 'машина';
    parts.push(`${member.count} ${kind}`);
  }
  if (!parts.length) return '—';
  if (!unit.members.length) return `${parts[0]} · без прицепных`;
  return parts.join(' + ');
}

/** Ширина агрегата для таблицы. Прочерк — это «никто не знает», а не ноль. */
export function widthLabel(width: number | null): string {
  if (width === null || width === undefined) return '—';
  return `${width.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} м`;
}

/**
 * Что показать в колонке «Норматив».
 *
 * «Нет» — это не ошибка, а работа: у машины не заполнена ширина захвата или в
 * книге нет строки под такой агрегат. Поэтому рядом с «нет» всегда живёт
 * причина (`missing_norm_reasons` с сервера), а не молчание.
 */
export function normLabel(unit: MachineryUnit): 'есть' | 'нет' {
  return unit.has_norm ? 'есть' : 'нет';
}

/** Подпись под таблицей: итог по ВЫБОРКЕ, а не по странице. */
export function unitsSummary(page: UnitsPage | undefined, shown: number): string | null {
  if (!page) return null;
  return `Показано ${shown} из ${page.total} · ${page.with_norm} с нормативом · ${page.without_norm} без`;
}

export type UnitDraftMember = { model_id: string; count: number; name: string };

/**
 * Черновик формы → тело запроса.
 *
 * Имя отправляем ТОЛЬКО если человек его правил: пустое поле значит «собери
 * из состава», и слать туда сгенерированное имя значит навсегда пометить его
 * как заданное руками — после этого состав поменяется, а имя нет.
 */
export function unitPayload(draft: {
  /** Пусто — компанию определит сервер: в тенанте с одной компанией это
   *  однозначно, а в холдинге он честно попросит уточнить. Слать `""`
   *  нельзя — это невалидный UUID и 422 на ровном месте. */
  companyId?: string;
  primeModelId: string | null;
  members: UnitDraftMember[];
  name: string;
  nameTouched: boolean;
  operationIds: string[];
}): Record<string, unknown> {
  return {
    ...(draft.companyId ? { company_id: draft.companyId } : {}),
    prime_model_id: draft.primeModelId,
    members: draft.members.map((m) => ({ model_id: m.model_id, count: m.count })),
    ...(draft.nameTouched && draft.name.trim() ? { name: draft.name.trim() } : {}),
    operation_ids: draft.operationIds,
  };
}

/** Предпросмотр имени в форме — тем же правилом, что и на сервере. */
export function previewUnitName(primeName: string | null, members: UnitDraftMember[]): string {
  const parts = members
    .filter((m) => m.name.trim())
    .map((m) => (m.count > 1 ? `${m.name.trim()} ×${m.count}` : m.name.trim()));
  const head = (primeName ?? '').trim();
  if (!parts.length) return head || 'Без названия';
  return head ? [head, ...parts].join(' + ') : parts.join(' + ');
}
